(function () {
  'use strict';

  const gsap = window.gsap;
  const { animProp, easePower4, easePower2 } = window.Universe;

  class SizeChart {
    constructor(openers, modal) {
      this.isOpen = false;
      this.isAnimating = false;
      this.triggerElement = null;
      this.DOM = undefined;

      this.onClose = () => {
        this.close();
      };

      this.onOpen = (event) => {
        this.triggerElement = event.currentTarget;
        this.open();
      };

      this.onKeyUp = (evt) => {
        if (evt.code === 'Escape') {
          this.close();
        }
      };

      this.onKeyDown = (evt) => {
        if (evt.key === 'Tab' && this.isOpen) {
          this.trapFocus(evt);
        }
      };

      this.onFocusIn = (evt) => {
        if (this.isOpen && !this.isAnimating && !this.DOM.content.contains(evt.target)) {
          evt.preventDefault();
          const focusableElements = this.getFocusableElements();
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          }
        }
      };

      this.init(openers, modal);
    }

    setup() {
      for (let i = 0; i < this.DOM.openers.length; i++) {
        this.DOM.openers[i].addEventListener('click', this.onOpen);
      }
      for (let i = 0; i < this.DOM.closers.length; i++) {
        this.DOM.closers[i].addEventListener('click', this.onClose);
      }

      this.close(true);
    }

    close(immediate = false) {
      if (!this.isAnimating) {
        this.isAnimating = true;

        const closeTl = gsap.timeline({
          onComplete: () => {
            this.isOpen = false;
            this.isAnimating = false;

            document.removeEventListener('keyup', this.onKeyUp);
            document.removeEventListener('keydown', this.onKeyDown);
            document.removeEventListener('focusin', this.onFocusIn);

            if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
              this.triggerElement.focus();
            }

            this.DOM.modal.setAttribute('aria-hidden', 'true');
            this.DOM.modal.setAttribute('inert', '');

            document.body.style.overflow = '';
          },
        });

        closeTl
          .to(this.DOM.content, {
            opacity: 0,
            y: -30,
            duration: animProp(0.5, immediate),
            ease: easePower2,
            onComplete: () => {
              gsap.set(this.DOM.content, {
                y: 30,
              });
            },
          })
          .to(
            this.DOM.overlay,
            {
              opacity: 0,
              duration: animProp(0.5, immediate),
              ease: easePower2,
            },
            animProp('-0.4', immediate)
          )
          .set(this.DOM.modal, {
            opacity: 0,
            pointerEvents: 'none',
            zIndex: -1,
          });
      }
    }

    open() {
      if (!this.isAnimating) {
        this.isAnimating = true;

        const openTl = gsap.timeline({
          onComplete: () => {
            this.isOpen = true;
            this.isAnimating = false;

            document.addEventListener('keyup', this.onKeyUp);
            document.addEventListener('keydown', this.onKeyDown);
            document.addEventListener('focusin', this.onFocusIn);

            this.DOM.modal.setAttribute('aria-hidden', 'false');
            this.DOM.modal.removeAttribute('inert');

            document.body.style.overflow = 'hidden';

            const focusableElements = this.getFocusableElements();
            if (focusableElements.length > 0) {
              focusableElements[0].focus();
            }
          },
        });

        openTl
          .set(this.DOM.modal, {
            zIndex: 999,
            pointerEvents: 'all',
            opacity: 1,
          })
          .to(this.DOM.overlay, {
            opacity: 1,
            duration: 0.5,
            ease: easePower2,
          })
          .to(
            this.DOM.content,
            {
              opacity: 1,
              y: 0,
              duration: 0.5,
              ease: easePower2,
            },
            '-=0.4'
          );
      }
    }

    getFocusableElements() {
      if (!this.DOM.content) return [];

      const selector =
        'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const elements = Array.from(this.DOM.content.querySelectorAll(selector));

      return elements.filter(function (el) {
        if (el.getAttribute('aria-hidden') === 'true') return false;
        if (el.closest('[aria-hidden="true"]')) return false;

        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;

        return true;
      });
    }

    trapFocus(evt) {
      const focusableElements = this.getFocusableElements();

      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (evt.shiftKey) {
        if (activeElement === firstFocusable || !this.DOM.content.contains(activeElement)) {
          evt.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (activeElement === lastFocusable || !this.DOM.content.contains(activeElement)) {
          evt.preventDefault();
          firstFocusable.focus();
        }
      }
    }

    destroy() {
      if (this.DOM) {
        document.removeEventListener('keyup', this.onKeyUp);
        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('focusin', this.onFocusIn);

        if (this.isOpen) {
          document.body.style.overflow = '';
          this.DOM.modal.setAttribute('aria-hidden', 'true');
          this.DOM.modal.setAttribute('inert', '');
        }

        this.DOM = undefined;
      }
    }

    init(openers, modal) {
      if (modal) {
        this.DOM = {};
        this.DOM.modal = modal;
        this.DOM.openers = openers;
        this.DOM.closers = this.DOM.modal.querySelectorAll('.js-single-product-size-modal-closer');
        this.DOM.overlay = this.DOM.modal.querySelector('.js-single-product-size-modal-overlay');
        this.DOM.content = this.DOM.modal.querySelector('.js-single-product-size-modal-content');

        this.isOpen = false;
        this.isAnimating = false;

        this.DOM.modal.setAttribute('aria-hidden', 'true');
        this.DOM.modal.setAttribute('inert', '');

        this.setup();
      }
    }
  }

  window.Universe.SizeChart = SizeChart;
})();
